// =============================================================================
// SKIP TO CONTENT — Accessibility Component
// =============================================================================
// A hidden link that becomes visible when a keyboard user presses Tab.
// It allows them to skip the navigation menu and jump straight to the
// main content — this is a WCAG 2.1 AA requirement.
//
// WITHOUT this component, a keyboard user would have to Tab through
// every single nav link before reaching the actual page content.
// With this, they press Tab once → see "Skip to main content" → press
// Enter → jump to the content area.
// =============================================================================

import type { ReactNode } from 'react';

interface SkipToContentProps {
  /** The ID of the main content element to skip to (default: "main-content") */
  targetId?: string;
  /** Custom link text (translated via i18n in the consuming app) */
  children?: ReactNode;
}

/**
 * Renders a skip-to-content link for keyboard accessibility.
 * Invisible until focused via Tab key, then appears prominently.
 *
 * USAGE in your root layout:
 *   <SkipToContent />
 *   <Navbar />
 *   <main id="main-content"> ... </main>
 */
export function SkipToContent({
  targetId = 'main-content',
  children = 'Skip to main content',
}: SkipToContentProps) {
  return (
    <a href={`#${targetId}`} className="sr-only sr-only-focusable" id="skip-to-content">
      {children}
    </a>
  );
}
